import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import OrgTreeView from "@/components/OrgTreeView";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Network, Search, Users } from "lucide-react";
import { toast } from "sonner";

export default function OrgChart() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tree, setTree] = useState([]);
  const [total, setTotal] = useState(0);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    let m = true;
    api
      .get("/org/tree")
      .then((r) => {
        if (!m) return;
        setTree(r.data.roots || []);
        setTotal(r.data.total || 0);
      })
      .finally(() => m && setLoading(false));
    return () => {
      m = false;
    };
  }, []);

  const validate = async () => {
    setValidating(true);
    try {
      const r = await api.get("/org/validate");
      setIssues(r.data.issues || []);
      toast.success(`Validation complete · ${r.data.issues.length} issue${r.data.issues.length === 1 ? "" : "s"} found`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="max-w-7xl space-y-6" data-testid="org-chart-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">Organisation</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5 inline-flex items-center gap-3">
            Org Chart <span className="text-slate-400 text-base font-mono">{total}</span>
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            The canonical reporting tree across your tenant. Click any node to drill into the profile.
          </p>
        </div>
        {hasRole(user, "tenant_admin", "hr_admin", "hrbp") && (
          <Button onClick={validate} disabled={validating} variant="outline" data-testid="org-validate-btn">
            <Search className="w-3.5 h-3.5 mr-2" /> {validating ? "Validating…" : "Validate org"}
          </Button>
        )}
      </div>

      {issues.length > 0 && (
        <div className="axia-card p-4 border-l-4 border-l-amber-400" data-testid="org-issues">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900">Issues detected</div>
              <ul className="mt-2 text-sm text-slate-700 space-y-1">
                {issues.map((i, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-xs">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
                      i.severity === "high"
                        ? "bg-rose-50 text-rose-700"
                        : i.severity === "medium"
                        ? "bg-amber-50 text-amber-700"
                        : "bg-slate-100 text-slate-600"
                    }`}>{i.type.replace(/_/g, " ")}</span>
                    <span className="text-slate-700 font-medium">{i.name}</span>
                    {i.count && <span className="text-slate-500">— {i.count} reports</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading tree…</div>
      ) : tree.length === 0 ? (
        <div className="axia-card p-12 text-center text-slate-500">
          <Network className="w-6 h-6 mx-auto text-slate-300" />
          <div className="font-display text-lg font-semibold text-slate-900 mt-3">No employees yet</div>
          <p className="text-sm mt-1">Bulk-import or invite employees to populate your org chart.</p>
        </div>
      ) : (
        <OrgTreeView roots={tree} onSelect={(n) => navigate(`/profile/${n.id}`)} />
      )}
    </div>
  );
}
